/**
 * Centralni UI texty web-adminu (blok 2), od kroku I1 tenky obal nad @reos/i18n
 * katalogem (klic `webAdmin.*` v cs.json/en.json). Web-admin komponenty
 * migrovane na `useLocale()`/`t()` primo (krok I1) uz tenhle modul nepouzivaji;
 * zbytek (36 puvodnich exportu) tu zustava pro zpetnou kompatibilitu, aby se
 * nemuselo prepisovat 36 importu najednou — kazdy export je @deprecated
 * s odkazem na svuj katalogovy klic. Novy UI text pridavejte rovnou do
 * `packages/platform/i18n/messages/cs.json` + `en.json`, ne sem.
 *
 * TERMINOLOGIE (docs/platform/ui/TERMINOLOGIE.md): UI pojem „zakazka"
 * odpovida domenovemu pojmu case (dd_case, caseId) — DOMENA SE NEPREJMENOVAVA.
 *
 * Vsechny hodnoty tady jsou explicitne `{ locale: 'cs' }` — tenhle modul nema
 * pristup k locale kontextu uzivatele (jsou to staticke konstanty vyhodnocene
 * pri importu, ne komponenty), takze drzi puvodni chovani (vzdy cesky), dokud
 * volajici misto nepreijde primo na `useLocale()`/`t()`.
 */

import { t } from '@reos/i18n';

export { fixDiacritics } from './diacritics-fixes.js';

/** Stav nehotove sekce — nikdy nepsat „neni hotova" (zadani BLOK 2, ukol 2). */
export const PREPARING_BADGE = t('webAdmin.preparing.badge', { locale: 'cs' });
export const PREPARING_SUBTITLE = t('webAdmin.preparing.subtitle', { locale: 'cs' });
export const PREPARING_WHAT_HEADING = t('webAdmin.preparing.whatHeading', { locale: 'cs' });

export interface PreparingSectionCopy {
  readonly title: string;
  /** 2-4 vety: co sekce dela a proc. */
  readonly perex: string;
  /** Odrazky: konkretni obsah sekce. */
  readonly bullets: readonly string[];
  /**
   * Nahled grafickeho navrhu (BLOK 7 CAST A) — cesta pod /public. Jen pro
   * sekce, kde v docs/platform/ui/_designs existuje navrh, ktery skutecne
   * odpovida obsahu (nazvy souboru v _designs casto neodpovidaji obsahu,
   * overeno vizualne 2.9.2026 — proto tu neni napr. Portaly ani Nemovitosti).
   */
  readonly designImage?: string;
}

/**
 * Popisy nehotovych sekci (BLOK 2 + rozsireno BLOK 2.5 na 9sekcni IA,
 * docs/platform/ui/NAVIGACE-A-IKONY.md). Klic = cesta polozky v nav.ts;
 * nav.spec.ts hlida, ze kazda `preparing: true` polozka ma zaznam tady.
 * Text ziji v katalogu pod `webAdmin.preparing.sections.<slug>` (slug bez
 * uvodniho lomitka).
 */
export const PREPARING_SECTIONS: Readonly<Record<string, PreparingSectionCopy>> = {
  "/pozornost": {
    title: t("webAdmin.preparing.sections.pozornost.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.pozornost.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.pozornost.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.pozornost.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.pozornost.bullets.2", { locale: 'cs' })],
    designImage: "/navrhy/pozornost.png",
  },
  "/nabidky-rezervace": {
    title: t("webAdmin.preparing.sections.nabidky-rezervace.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.nabidky-rezervace.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.nabidky-rezervace.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.nabidky-rezervace.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.nabidky-rezervace.bullets.2", { locale: 'cs' })],
  },

  "/publikace": {
    title: t("webAdmin.preparing.sections.publikace.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.publikace.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.publikace.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.publikace.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.publikace.bullets.2", { locale: 'cs' })],
  },
  "/statistiky-inzerce": {
    title: t("webAdmin.preparing.sections.statistiky-inzerce.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.statistiky-inzerce.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.statistiky-inzerce.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.statistiky-inzerce.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.statistiky-inzerce.bullets.2", { locale: 'cs' })],
  },
  "/ohlasy-portalu": {
    title: t("webAdmin.preparing.sections.ohlasy-portalu.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.ohlasy-portalu.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.ohlasy-portalu.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.ohlasy-portalu.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.ohlasy-portalu.bullets.2", { locale: 'cs' })],
  },
  "/aukce": {
    title: t("webAdmin.preparing.sections.aukce.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.aukce.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.aukce.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.aukce.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.aukce.bullets.2", { locale: 'cs' })],
  },
  "/web-kancelare": {
    title: t("webAdmin.preparing.sections.web-kancelare.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.web-kancelare.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.web-kancelare.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.web-kancelare.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.web-kancelare.bullets.2", { locale: 'cs' })],
  },
  "/microsite": {
    title: t("webAdmin.preparing.sections.microsite.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.microsite.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.microsite.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.microsite.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.microsite.bullets.2", { locale: 'cs' })],
  },
  "/vizitka-maklere": {
    title: t("webAdmin.preparing.sections.vizitka-maklere.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.vizitka-maklere.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.vizitka-maklere.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.vizitka-maklere.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.vizitka-maklere.bullets.2", { locale: 'cs' })],
  },
  "/weby-projektu": {
    title: t("webAdmin.preparing.sections.weby-projektu.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.weby-projektu.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.weby-projektu.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.weby-projektu.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.weby-projektu.bullets.2", { locale: 'cs' })],
  },
  "/klientsky-portal": {
    title: t("webAdmin.preparing.sections.klientsky-portal.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.klientsky-portal.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.klientsky-portal.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.klientsky-portal.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.klientsky-portal.bullets.2", { locale: 'cs' })],
  },
  "/schranka": {
    title: t("webAdmin.preparing.sections.schranka.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.schranka.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.schranka.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.schranka.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.schranka.bullets.2", { locale: 'cs' })],
  },
  "/mailing": {
    title: t("webAdmin.preparing.sections.mailing.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.mailing.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.mailing.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.mailing.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.mailing.bullets.2", { locale: 'cs' })],
  },
  "/sms": {
    title: t("webAdmin.preparing.sections.sms.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.sms.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.sms.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.sms.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.sms.bullets.2", { locale: 'cs' })],
  },
  "/notifikace": {
    title: t("webAdmin.preparing.sections.notifikace.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.notifikace.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.notifikace.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.notifikace.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.notifikace.bullets.2", { locale: 'cs' })],
  },
  "/emailove-schranky": {
    title: t("webAdmin.preparing.sections.emailove-schranky.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.emailove-schranky.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.emailove-schranky.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.emailove-schranky.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.emailove-schranky.bullets.2", { locale: 'cs' })],
  },
  "/dokumenty": {
    title: t("webAdmin.preparing.sections.dokumenty.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.dokumenty.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.dokumenty.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.dokumenty.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.dokumenty.bullets.2", { locale: 'cs' }), t("webAdmin.preparing.sections.dokumenty.bullets.3", { locale: 'cs' })],
    designImage: "/navrhy/dokumenty-a-reporty.png",
  },
  "/naklady-nemovitosti": {
    title: t("webAdmin.preparing.sections.naklady-nemovitosti.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.naklady-nemovitosti.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.naklady-nemovitosti.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.naklady-nemovitosti.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.naklady-nemovitosti.bullets.2", { locale: 'cs' })],
  },
  "/provize": {
    title: t("webAdmin.preparing.sections.provize.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.provize.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.provize.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.provize.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.provize.bullets.2", { locale: 'cs' })],
  },
  "/banka-platby": {
    title: t("webAdmin.preparing.sections.banka-platby.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.banka-platby.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.banka-platby.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.banka-platby.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.banka-platby.bullets.2", { locale: 'cs' })],
  },
  "/statistiky": {
    title: t("webAdmin.preparing.sections.statistiky.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.statistiky.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.statistiky.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.statistiky.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.statistiky.bullets.2", { locale: 'cs' }), t("webAdmin.preparing.sections.statistiky.bullets.3", { locale: 'cs' })],
    designImage: "/navrhy/statistiky.png",
  },
  "/trh-srovnani": {
    title: t("webAdmin.preparing.sections.trh-srovnani.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.trh-srovnani.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.trh-srovnani.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.trh-srovnani.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.trh-srovnani.bullets.2", { locale: 'cs' })],
  },
  "/ocenovani": {
    title: t("webAdmin.preparing.sections.ocenovani.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.ocenovani.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.ocenovani.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.ocenovani.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.ocenovani.bullets.2", { locale: 'cs' })],
  },
  "/zdroje-klientu": {
    title: t("webAdmin.preparing.sections.zdroje-klientu.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.zdroje-klientu.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.zdroje-klientu.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.zdroje-klientu.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.zdroje-klientu.bullets.2", { locale: 'cs' })],
  },
  "/uzivatele-role": {
    title: t("webAdmin.preparing.sections.uzivatele-role.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.uzivatele-role.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.uzivatele-role.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.uzivatele-role.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.uzivatele-role.bullets.2", { locale: 'cs' })],
  },
  "/pobocky-tymy": {
    title: t("webAdmin.preparing.sections.pobocky-tymy.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.pobocky-tymy.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.pobocky-tymy.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.pobocky-tymy.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.pobocky-tymy.bullets.2", { locale: 'cs' })],
  },
  "/zdroje": {
    title: t("webAdmin.preparing.sections.zdroje.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.zdroje.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.zdroje.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.zdroje.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.zdroje.bullets.2", { locale: 'cs' })],
  },
  "/import-migrace": {
    title: t("webAdmin.preparing.sections.import-migrace.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.import-migrace.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.import-migrace.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.import-migrace.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.import-migrace.bullets.2", { locale: 'cs' })],
  },
  "/gdpr": {
    title: t("webAdmin.preparing.sections.gdpr.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.gdpr.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.gdpr.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.gdpr.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.gdpr.bullets.2", { locale: 'cs' })],
  },
  "/zalohy-provoz": {
    title: t("webAdmin.preparing.sections.zalohy-provoz.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.zalohy-provoz.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.zalohy-provoz.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.zalohy-provoz.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.zalohy-provoz.bullets.2", { locale: 'cs' })],
  },
  "/domeny-hosting": {
    title: t("webAdmin.preparing.sections.domeny-hosting.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.domeny-hosting.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.domeny-hosting.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.domeny-hosting.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.domeny-hosting.bullets.2", { locale: 'cs' })],
  },
};

/**
 * Zalozky detailu zakazky (BLOK 2, rozsireno BLOK 6 dle MODEL-ZAKAZKY-ROZHODNUTI.md):
 * audit je faze zakazky, ne samostatny objekt. Zalozky faze naber/audit jsou
 * aktivni; zalozky pozdejsich fazi (inzerce, obchod, zaver) se zobrazuji
 * tlumene se stavem PRIPRAVUJE SE, aby maklet videl, co prijde (DOPLNEK R.B. 1.9.).
 */
/**
 * Skupiny navigace detailu (Milnik 1, bod 3). 19 rovnocennych zalozek se
 * neveslo do listy a michalo hotove sekce s placeholdery; skupiny drzi
 * poradi z navrhu cilove IA. Skupina `obchod` sdruzuje vsechny zalozky
 * pozdejsi faze do JEDNE polozky mimo hlavni navigaci.
 */
export const CASE_TAB_GROUPS = [
  { id: 'kokpit', label: t('webAdmin.caseTabGroups.kokpit.label', { locale: 'cs' }) },
  { id: 'audit', label: t('webAdmin.caseTabGroups.audit.label', { locale: 'cs' }) },
  { id: 'nemovitost', label: t('webAdmin.caseTabGroups.nemovitost.label', { locale: 'cs' }) },
  { id: 'dokumenty', label: t('webAdmin.caseTabGroups.dokumenty.label', { locale: 'cs' }) },
  { id: 'historie', label: t('webAdmin.caseTabGroups.historie.label', { locale: 'cs' }) },
  { id: 'obchod', label: t('webAdmin.caseTabGroups.obchod.label', { locale: 'cs' }) },
] as const;

export type CaseTabGroupId = (typeof CASE_TAB_GROUPS)[number]['id'];

export const CASE_TABS = [
  { id: 'prehled', label: t('webAdmin.caseTabs.prehled.label', { locale: 'cs' }), phase: 'audit', group: 'kokpit' },
  { id: 'co-vime', label: t('webAdmin.caseTabs.co-vime.label', { locale: 'cs' }), phase: 'audit', group: 'audit' },
  { id: 'co-nevime', label: t('webAdmin.caseTabs.co-nevime.label', { locale: 'cs' }), phase: 'audit', group: 'audit' },
  { id: 'konflikty', label: t('webAdmin.caseTabs.konflikty.label', { locale: 'cs' }), phase: 'audit', group: 'audit' },
  { id: 'oceneni', label: t('webAdmin.caseTabs.oceneni.label', { locale: 'cs' }), phase: 'audit', group: 'audit' },
  { id: 'geometrie', label: t('webAdmin.caseTabs.geometrie.label', { locale: 'cs' }), phase: 'audit', group: 'nemovitost' },
  { id: 'pristup', label: t('webAdmin.caseTabs.pristup.label', { locale: 'cs' }), phase: 'audit', group: 'nemovitost' },
  { id: 'site', label: t('webAdmin.caseTabs.site.label', { locale: 'cs' }), phase: 'audit', group: 'nemovitost' },
  { id: 'uzemni-plan', label: t('webAdmin.caseTabs.uzemni-plan.label', { locale: 'cs' }), phase: 'audit', group: 'nemovitost' },
  { id: 'rizika', label: t('webAdmin.caseTabs.rizika.label', { locale: 'cs' }), phase: 'audit', group: 'nemovitost' },
  { id: 'dokumenty', label: t('webAdmin.caseTabs.dokumenty.label', { locale: 'cs' }), phase: 'audit', group: 'dokumenty' },
  { id: 'aktivity', label: t('webAdmin.caseTabs.aktivity.label', { locale: 'cs' }), phase: 'audit', group: 'historie' },
  { id: 'inzerce', label: t('webAdmin.caseTabs.inzerce.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  { id: 'poptavky', label: t('webAdmin.caseTabs.poptavky.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  // Krok CRM3: zalozka „Terminy" — vytvoreni udalosti v kalendari maklere
  // navazane na tuto zakazku. Stoji pred „Prohlidkami", protoze prohlidka
  // JE typ udalosti, ktery se tu planuje; seznam uskutecnenych prohlidek
  // zustava na zalozce „Prohlidky" (CRM4).
  { id: 'terminy', label: t('webAdmin.caseTabs.terminy.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  { id: 'prohlidky', label: t('webAdmin.caseTabs.prohlidky.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  { id: 'nabidky', label: t('webAdmin.caseTabs.nabidky.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  { id: 'smlouva', label: t('webAdmin.caseTabs.smlouva.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  { id: 'naklady', label: t('webAdmin.caseTabs.naklady.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
  { id: 'provize', label: t('webAdmin.caseTabs.provize.label', { locale: 'cs' }), phase: 'later', group: 'obchod' },
] as const;

/** Vychozi sekce detailu; sem vede i neplatna hodnota `?sekce=`. */
export const CASE_TAB_DEFAULT = 'prehled';

/** Je `value` platne id zalozky? Vstup z URL nikdy neverit. */
export function isCaseTabId(value: unknown): boolean {
  return typeof value === 'string' && CASE_TABS.some((t) => t.id === value);
}

export type CaseTabId = (typeof CASE_TABS)[number]['id'];

/** Zalozky faze naber/audit — aktivni dnes (DOPLNEK R.B., MODEL-ZAKAZKY-ROZHODNUTI.md). */
export const CASE_TABS_AUDIT_PHASE: readonly CaseTabId[] = CASE_TABS.filter((t) => t.phase === 'audit').map((t) => t.id);

/**
 * Krok UXP5b bod 1 — SEKCE, KTERE NESTOJI NA PROVĚŘENÍ.
 *
 * Obsah zalozek obchodni faze (inzerce, poptavky, terminy, smlouva, provize,
 * naklady…) cte zakazku, nemovitost a doklady — NE vysledek auditu. Detail je
 * proto musi otevrit i u zakazky, nad kterou zadne proverení jeste nebezelo
 * (jinak makler z karty „Peníze" nikam nedojde, viz `zakazka-detail.tsx`).
 */
export const CASE_TABS_WITHOUT_AUDIT: readonly CaseTabId[] = CASE_TABS.filter((t) => t.phase === 'later').map((t) => t.id);

/**
 * Krok PIL3 bod 8 (nalez F07) — OBLASTI ZAKAZKY, KTERE REOS JESTE NEMA.
 *
 * Detail je kresli jako „Připravuje se na produkční provoz" (`PreparingSection`
 * v `zakazka-detail.tsx`) a postup zakazky je NESMI ODSKRTNOUT: „✓ Inzerce"
 * u sekce, kterou uzivatelka nemuze otevrit, tvrdi, ze se oblast odbavila.
 * Jeden seznam pro obe mista — druhy by se s obrazovkou rozesel (test
 * `zakazka-detail.spec.tsx` drzi, ze kazde id odsud opravdu kresli placeholder).
 *
 * `phase: 'later'` v `CASE_TABS` tenhle seznam NAHRADIT NEMUZE: pozdejsi fazi
 * ma i „Poptávky", „Termíny" a „Prohlídky", ktere uz davno bezi (viz komentare
 * u `CASE_TAB_PREPARING`).
 *
 * Krok UI3: „Inzerce" z seznamu ODESLA — zalozka ma karte „Jak se inzerat
 * dari" (statistiky, report pro majitele, import CSV), takze „✓ Inzerce"
 * uz nelze. Prazdny seznam je poctivy stav, ne zapomenuta hodnota.
 */
export const CASE_AREAS_PREPARING: readonly CaseTabId[] = [];

/**
 * Krok UXR2 — SEST ZALOZEK PREDLOHY (`03-detail-zakazky.html`):
 * Kokpit · Nemovitost · Dokumenty · Smlouvy · Fakturace · Historie.
 *
 * `CASE_TABS` (devatenact sekci) se NEMENI — meni se jen navigace nad nimi.
 * Kazda zalozka predlohy vlastni jednu nebo vic sekci (`members`); kdyz jich
 * ma vic, vykresli se pod listou druha uroven, presne jako do UXR2.
 *
 * Sekce, ktere predloha v liste nema (`CASE_OVERFLOW_TABS`), nezmizely —
 * jdou z nabidky „⋯" v hlavicce. Patri tam cely AUDIT (zadani UXR2: „Audit
 * jen jako odkaz »Podrobnosti ověření«, ne záložka") a zbytek obchodni faze
 * (Inzerce, Poptávky, Termíny, Prohlídky, Nabídky). Zahodit je kvuli vzhledu
 * by znamenalo zahodit funkce z kroku CRM1/CRM3 — viz ROZHODNUTI UXR2-1.
 */
export const CASE_PRIMARY_TABS = [
  { id: 'kokpit', label: t('webAdmin.caseTabGroups.kokpit.label', { locale: 'cs' }), members: ['prehled'] },
  {
    id: 'nemovitost',
    label: t('webAdmin.caseTabGroups.nemovitost.label', { locale: 'cs' }),
    members: ['geometrie', 'pristup', 'site', 'uzemni-plan', 'rizika'],
  },
  { id: 'dokumenty', label: t('webAdmin.caseTabGroups.dokumenty.label', { locale: 'cs' }), members: ['dokumenty'] },
  { id: 'smlouvy', label: t('webAdmin.caseTabs.smlouva.label', { locale: 'cs' }), members: ['smlouva'] },
  // Krok UXR3: zalozka se jmenuje „Peníze" (predloha `Zakazka.dc.html`) a
  // sdruzuje Provizi I Naklady. Id zustava `fakturace`, aby se nezmenila
  // `data-testid` ani odkazy, ktere na zalozku uz miri (zadani UXR3 bod 7).
  {
    id: 'fakturace',
    label: t('webAdmin.caseTabGroups.penize.label', { locale: 'cs' }),
    members: ['provize', 'naklady'],
  },
  { id: 'historie', label: t('webAdmin.caseTabGroups.historie.label', { locale: 'cs' }), members: ['aktivity'] },
] as const satisfies readonly { id: string; label: string; members: readonly CaseTabId[] }[];

export type CasePrimaryTabId = (typeof CASE_PRIMARY_TABS)[number]['id'];

/** Sekce, ktera se v liste predlohy neukazuje — otevira ji „⋯" (viz vyse). */
export const CASE_OVERFLOW_TABS: readonly CaseTabId[] = CASE_TABS.map((tab) => tab.id).filter(
  (id) => !CASE_PRIMARY_TABS.some((primary) => (primary.members as readonly string[]).includes(id)),
);

/** Sekce auditu, na kterou vede odkaz „Podrobnosti ověření". */
export const CASE_AUDIT_ENTRY_TAB: CaseTabId = 'co-vime';

/** Ktera zalozka predlohy je prave otevrena? `null` = sekce z „⋯". */
export function casePrimaryTabOf(tab: CaseTabId): CasePrimaryTabId | null {
  return (
    CASE_PRIMARY_TABS.find((primary) => (primary.members as readonly string[]).includes(tab))?.id ?? null
  );
}

/**
 * Popis obsahu zalozky pro stav PRIPRAVUJE SE — zalozka bez dat nikdy neni
 * prazdna stranka (zadani BLOK 2, ukol 3 bod 2). Text zalozek pozdejsich fazi
 * (inzerce/obchod/zaver) je stejny katalogovy klic jako hlavni navigace
 * (`webAdmin.preparing.sections.*`), aby text existoval v katalogu jen jednou.
 */
export const CASE_TAB_PREPARING: Readonly<Record<CaseTabId, PreparingSectionCopy>> = {
  prehled: {
    title: t("webAdmin.casePreparing.prehled.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.prehled.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.prehled.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.prehled.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.prehled.bullets.2", { locale: 'cs' })],
  },
  "co-vime": {
    title: t("webAdmin.casePreparing.co-vime.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.co-vime.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.co-vime.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.co-vime.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.co-vime.bullets.2", { locale: 'cs' })],
  },
  "co-nevime": {
    title: t("webAdmin.casePreparing.co-nevime.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.co-nevime.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.co-nevime.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.co-nevime.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.co-nevime.bullets.2", { locale: 'cs' })],
  },
  konflikty: {
    title: t("webAdmin.casePreparing.konflikty.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.konflikty.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.konflikty.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.konflikty.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.konflikty.bullets.2", { locale: 'cs' })],
  },
  oceneni: {
    title: t("webAdmin.casePreparing.oceneni.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.oceneni.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.oceneni.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.oceneni.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.oceneni.bullets.2", { locale: 'cs' })],
  },
  geometrie: {
    title: t("webAdmin.casePreparing.geometrie.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.geometrie.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.geometrie.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.geometrie.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.geometrie.bullets.2", { locale: 'cs' })],
  },
  pristup: {
    title: t("webAdmin.casePreparing.pristup.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.pristup.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.pristup.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.pristup.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.pristup.bullets.2", { locale: 'cs' })],
  },
  site: {
    title: t("webAdmin.casePreparing.site.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.site.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.site.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.site.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.site.bullets.2", { locale: 'cs' })],
  },
  "uzemni-plan": {
    title: t("webAdmin.casePreparing.uzemni-plan.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.uzemni-plan.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.uzemni-plan.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.uzemni-plan.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.uzemni-plan.bullets.2", { locale: 'cs' })],
  },
  rizika: {
    title: t("webAdmin.casePreparing.rizika.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.rizika.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.rizika.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.rizika.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.rizika.bullets.2", { locale: 'cs' })],
  },
  dokumenty: {
    title: t("webAdmin.casePreparing.dokumenty.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.dokumenty.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.dokumenty.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.dokumenty.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.dokumenty.bullets.2", { locale: 'cs' })],
  },
  aktivity: {
    title: t("webAdmin.casePreparing.aktivity.title", { locale: 'cs' }),
    perex: t("webAdmin.casePreparing.aktivity.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.casePreparing.aktivity.bullets.0", { locale: 'cs' }), t("webAdmin.casePreparing.aktivity.bullets.1", { locale: 'cs' }), t("webAdmin.casePreparing.aktivity.bullets.2", { locale: 'cs' })],
  },
  // Later case phases (DOPLNEK R.B. 1.9.) share copy with the main navigation (nav.ts) — text lives once.
  inzerce: PREPARING_SECTIONS["/publikace"] as PreparingSectionCopy,
  // Krok CRM1: zalozka „Poptavky" v zakazce uz neni PREPARING (zobrazuje
  // realne zajemce o nemovitost), ale `CASE_TAB_PREPARING` musi zustat
  // kompletni Record - text proto zustava v katalogu preparing sekce, jen
  // se nesdili s POLOZKOU MENU (viz `CASE_TAB_PREPARING` niže).
  poptavky: {
    title: t("webAdmin.preparing.sections.poptavky.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.poptavky.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.poptavky.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.poptavky.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.poptavky.bullets.2", { locale: 'cs' })],
  },
  // Krok CRM3: zalozka „Terminy" uz neni placeholder (viz zakazka-detail.tsx
  // tabContent), ale `CASE_TAB_PREPARING` musi zustat kompletni Record.
  // Text je tady zkopirovaný z katalogu preparing sekce `/kalendar`, ktery
  // CRM3 smazal spolu s odemykanim polozky menu (nav.spec.ts drzi, ze
  // ne-preparing polozka v `PREPARING_SECTIONS` neni) — stejne jako
  // `poptavky` a `smlouva` nad.
  terminy: {
    title: t("webAdmin.preparing.sections.kalendar.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.kalendar.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.kalendar.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.kalendar.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.kalendar.bullets.2", { locale: 'cs' })],
  },
  prohlidky: PREPARING_SECTIONS["/prohlidky"] as PreparingSectionCopy,
  nabidky: PREPARING_SECTIONS["/nabidky-rezervace"] as PreparingSectionCopy,
  // Krok T3: zalozka "Smlouva" uz neni placeholder (viz zakazka-detail.tsx
  // tabContent), ale CASE_TAB_PREPARING musi zustat kompletni Record - text
  // zustava z puvodniho katalogu preparing sekci (uz nezavisly na PREPARING_SECTIONS).
  smlouva: {
    title: t("webAdmin.preparing.sections.smlouvy-sablony.title", { locale: 'cs' }),
    perex: t("webAdmin.preparing.sections.smlouvy-sablony.perex", { locale: 'cs' }),
    bullets: [t("webAdmin.preparing.sections.smlouvy-sablony.bullets.0", { locale: 'cs' }), t("webAdmin.preparing.sections.smlouvy-sablony.bullets.1", { locale: 'cs' }), t("webAdmin.preparing.sections.smlouvy-sablony.bullets.2", { locale: 'cs' })],
  },
  naklady: PREPARING_SECTIONS["/naklady-nemovitosti"] as PreparingSectionCopy,
  provize: PREPARING_SECTIONS["/provize"] as PreparingSectionCopy,
};

/** Faze zakazky (MODEL-ZAKAZKY-ROZHODNUTI.md) — dnes odvoditelne jen prvni tri z dat R3 behu. */
export type CasePhaseKey = 'KONCEPT' | 'AUDIT_PROBIHA' | 'AUDIT_DOKONCENO';

export const CASE_PHASE_CAPTIONS: Readonly<Record<CasePhaseKey, string>> = {
  KONCEPT: t('webAdmin.casePhaseCaptions.KONCEPT', { locale: 'cs' }),
  AUDIT_PROBIHA: t('webAdmin.casePhaseCaptions.AUDIT_PROBIHA', { locale: 'cs' }),
  // „Audit dokoncen" byl nepravdivy: dokoncil se PRUZKUM (research run),
  // pripad ceka na doplneni a zadna povinna brana neni zavrena (Milnik 1 bod 1).
  AUDIT_DOKONCENO: t('webAdmin.casePhaseCaptions.AUDIT_DOKONCENO', { locale: 'cs' }),
};

/** Spolecne texty terminologie „zakazka" (UI pojem; domena zustava case). */
export const CASE_TEXTS = {
  sectionTitle: t('webAdmin.caseTexts.sectionTitle', { locale: 'cs' }),
  breadcrumbRoot: t('webAdmin.caseTexts.breadcrumbRoot', { locale: 'cs' }),
  listCaption: t('webAdmin.caseTexts.listCaption', { locale: 'cs' }),
  // BLOK 5: seznam uz muze obsahovat i skutecne koncepty uzivatele, ne jen demo fixtures.
  listCountSuffix: t('webAdmin.caseTexts.listCountSuffix', { locale: 'cs' }),
  columnCase: t('webAdmin.caseTexts.columnCase', { locale: 'cs' }),
  emptyTitle: t('webAdmin.caseTexts.emptyTitle', { locale: 'cs' }),
  emptyDescription: t('webAdmin.caseTexts.emptyDescription', { locale: 'cs' }),
  emptyFilteredTitle: t('webAdmin.caseTexts.emptyFilteredTitle', { locale: 'cs' }),
  emptyFilteredDescription: t('webAdmin.caseTexts.emptyFilteredDescription', { locale: 'cs' }),
  loadError: t('webAdmin.caseTexts.loadError', { locale: 'cs' }),
  listLoadError: t('webAdmin.caseTexts.listLoadError', { locale: 'cs' }),
  loading: t('webAdmin.caseTexts.loading', { locale: 'cs' }),
  searchPlaceholder: t('webAdmin.caseTexts.searchPlaceholder', { locale: 'cs' }),
  /** Zakazka bez auditniho behu — stav, ne chyba (krok S3). */
  noAuditRun: t('webAdmin.caseTexts.noAuditRun', { locale: 'cs' }),
  /** V10 zivy pruchod: neplatne/neexistujici id v URL → 404, ne vecne „Načítám…". */
  notFoundTitle: t('webAdmin.caseTexts.notFoundTitle', { locale: 'cs' }),
  notFoundDetail: t('webAdmin.caseTexts.notFoundDetail', { locale: 'cs' }),
  notFoundBack: t('webAdmin.caseTexts.notFoundBack', { locale: 'cs' }),
} as const;

/** Texty seznamu zakazek (BLOK 4): toolbar, KPI pas, sloupce, dlazdice. */
export const CASE_LIST_TEXTS = {
  newCase: t('webAdmin.caseListTexts.newCase', { locale: 'cs' }),
  searchPlaceholder: t('webAdmin.caseListTexts.searchPlaceholder', { locale: 'cs' }),
  searchLabel: t('webAdmin.caseListTexts.searchLabel', { locale: 'cs' }),
  filtersButton: t('webAdmin.caseListTexts.filtersButton', { locale: 'cs' }),
  savedFiltersButton: t('webAdmin.caseListTexts.savedFiltersButton', { locale: 'cs' }),
  savedFiltersUnavailable: t('webAdmin.caseListTexts.savedFiltersUnavailable', { locale: 'cs' }),
  clearFilters: t('webAdmin.caseListTexts.clearFilters', { locale: 'cs' }),
  viewTable: t('webAdmin.caseListTexts.viewTable', { locale: 'cs' }),
  viewCards: t('webAdmin.caseListTexts.viewCards', { locale: 'cs' }),
  viewToggleLabel: t('webAdmin.caseListTexts.viewToggleLabel', { locale: 'cs' }),
  kindLabel: t('webAdmin.caseListTexts.kindLabel', { locale: 'cs' }),
  statusLabel: t('webAdmin.caseListTexts.statusLabel', { locale: 'cs' }),
  kindAll: t('webAdmin.caseListTexts.kindAll', { locale: 'cs' }),
  statusAll: t('webAdmin.caseListTexts.statusAll', { locale: 'cs' }),
  searchChipPrefix: t('webAdmin.caseListTexts.searchChipPrefix', { locale: 'cs' }),
  kpiActive: t('webAdmin.caseListTexts.kpiActive', { locale: 'cs' }),
  kpiBlocked: t('webAdmin.caseListTexts.kpiBlocked', { locale: 'cs' }),
  kpiReady: t('webAdmin.caseListTexts.kpiReady', { locale: 'cs' }),
  columnKind: t('webAdmin.caseListTexts.columnKind', { locale: 'cs' }),
  columnClient: t('webAdmin.caseListTexts.columnClient', { locale: 'cs' }),
  columnStatus: t('webAdmin.caseListTexts.columnStatus', { locale: 'cs' }),
  columnReadiness: t('webAdmin.caseListTexts.columnReadiness', { locale: 'cs' }),
  columnConfidence: t('webAdmin.caseListTexts.columnConfidence', { locale: 'cs' }),
  columnOpenQuestions: t('webAdmin.caseListTexts.columnOpenQuestions', { locale: 'cs' }),
  columnRisks: t('webAdmin.caseListTexts.columnRisks', { locale: 'cs' }),
  columnValuation: t('webAdmin.caseListTexts.columnValuation', { locale: 'cs' }),
  columnNextStep: t('webAdmin.caseListTexts.columnNextStep', { locale: 'cs' }),
  columnLastActivity: t('webAdmin.caseListTexts.columnLastActivity', { locale: 'cs' }),
  columnActions: t('webAdmin.caseListTexts.columnActions', { locale: 'cs' }),
  rowMenuLabel: t('webAdmin.caseListTexts.rowMenuLabel', { locale: 'cs' }),
  rowMenuOpen: t('webAdmin.caseListTexts.rowMenuOpen', { locale: 'cs' }),
  riskNone: t('webAdmin.caseListTexts.riskNone', { locale: 'cs' }),
  riskLow: t('webAdmin.caseListTexts.riskLow', { locale: 'cs' }),
  riskMedium: t('webAdmin.caseListTexts.riskMedium', { locale: 'cs' }),
  riskHigh: t('webAdmin.caseListTexts.riskHigh', { locale: 'cs' }),
  rowsPerPage: t('webAdmin.caseListTexts.rowsPerPage', { locale: 'cs' }),
  unknown: t('webAdmin.caseListTexts.unknown', { locale: 'cs' }),
  pilotBadge: t('webAdmin.caseListTexts.pilotBadge', { locale: 'cs' }),
  demoBadge: t('webAdmin.caseListTexts.demoBadge', { locale: 'cs' }),
} as const;

/**
 * Texty obrazovky „Založit zakázku" (krok UXZ1; do nej ctyrkrokovy pruvodce
 * BLOK 5). Ilustrace druhu nemovitosti zustavaji tam, kde nesou informaci —
 * v seznamu a detailu zakazky (`CASE_KIND_ILLUSTRATION`).
 */
export const WIZARD_TEXTS = {
  pageTitle: t('webAdmin.wizard.pageTitle', { locale: 'cs' }),
  typeLabel: t('webAdmin.wizard.typeLabel', { locale: 'cs' }),
  typeLand: t('webAdmin.wizard.typeLand', { locale: 'cs' }),
  typeHouse: t('webAdmin.wizard.typeHouse', { locale: 'cs' }),
  typeApartment: t('webAdmin.wizard.typeApartment', { locale: 'cs' }),
  typeOther: t('webAdmin.wizard.typeOther', { locale: 'cs' }),
  intentLabel: t('webAdmin.wizard.intentLabel', { locale: 'cs' }),
  intentSale: t('webAdmin.wizard.intentSale', { locale: 'cs' }),
  intentRent: t('webAdmin.wizard.intentRent', { locale: 'cs' }),
  addressLabel: t('webAdmin.wizard.addressLabel', { locale: 'cs' }),
  addressPlaceholder: t('webAdmin.wizard.addressPlaceholder', { locale: 'cs' }),
  addressSourceCaption: t('webAdmin.wizard.addressSourceCaption', { locale: 'cs' }),
  mapUnavailable: t('webAdmin.wizard.mapUnavailable', { locale: 'cs' }),
  parcelNumberLabel: t('webAdmin.wizard.parcelNumberLabel', { locale: 'cs' }),
  cadastralAreaLabel: t('webAdmin.wizard.cadastralAreaLabel', { locale: 'cs' }),
  landRegistryLabel: t('webAdmin.wizard.landRegistryLabel', { locale: 'cs' }),
  listingUrlLabel: t('webAdmin.wizard.listingUrlLabel', { locale: 'cs' }),
  listingUrlPlaceholder: t('webAdmin.wizard.listingUrlPlaceholder', { locale: 'cs' }),
  // Krok B1: odkaz na inzerat formular jen PREDVYPLNI, clovek ho potvrdi.
  listingLoadButton: t('webAdmin.wizard.listingLoadButton', { locale: 'cs' }),
  listingLoading: t('webAdmin.wizard.listingLoading', { locale: 'cs' }),
  listingLoaded: t('webAdmin.wizard.listingLoaded', { locale: 'cs' }),
  listingError: t('webAdmin.wizard.listingError', { locale: 'cs' }),
  unitNumberLabel: t('webAdmin.wizard.unitNumberLabel', { locale: 'cs' }),
  // Krok UXP4a bod 4 (nalez P2-17): dispozice bytu, u pozemku a domu nic.
  layoutLabel: t('webAdmin.wizard.layoutLabel', { locale: 'cs' }),
  layoutPlaceholder: t('webAdmin.wizard.layoutPlaceholder', { locale: 'cs' }),
  cadastralFromAddress: t('webAdmin.wizard.cadastralFromAddress', { locale: 'cs' }),
  notesLabel: t('webAdmin.wizard.notesLabel', { locale: 'cs' }),
  notesPlaceholder: t('webAdmin.wizard.notesPlaceholder', { locale: 'cs' }),
  clientLabel: t('webAdmin.wizard.clientLabel', { locale: 'cs' }),
  clientPerson: t('webAdmin.wizard.clientPerson', { locale: 'cs' }),
  clientCompany: t('webAdmin.wizard.clientCompany', { locale: 'cs' }),
  clientPersonNameLabel: t('webAdmin.wizard.clientPersonNameLabel', { locale: 'cs' }),
  icoLabel: t('webAdmin.wizard.icoLabel', { locale: 'cs' }),
  aresButton: t('webAdmin.wizard.aresButton', { locale: 'cs' }),
  aresLoading: t('webAdmin.wizard.aresLoading', { locale: 'cs' }),
  aresFound: t('webAdmin.wizard.aresFound', { locale: 'cs' }),
  aresNotFound: t('webAdmin.wizard.aresNotFound', { locale: 'cs' }),
  aresError: t('webAdmin.wizard.aresError', { locale: 'cs' }),
  aresInvalidIco: t('webAdmin.wizard.aresInvalidIco', { locale: 'cs' }),
  companyNameLabel: t('webAdmin.wizard.companyNameLabel', { locale: 'cs' }),
  companyAddressLabel: t('webAdmin.wizard.companyAddressLabel', { locale: 'cs' }),
  companyAddressAresHint: t('webAdmin.wizard.companyAddressAresHint', { locale: 'cs' }),
  brokerLabel: t('webAdmin.wizard.brokerLabel', { locale: 'cs' }),
  brokerUnassigned: t('webAdmin.wizard.brokerUnassigned', { locale: 'cs' }),
  priceExpectationLabel: t('webAdmin.wizard.priceExpectationLabel', { locale: 'cs' }),
  // Krok PROP3 bod 3: „Toto je nabídková cena" — bez zaskrtnuti zustane cislo
  // jen predstavou majitele.
  priceIsAskingLabel: t('webAdmin.wizard.priceIsAskingLabel', { locale: 'cs' }),
  priceIsAskingHint: t('webAdmin.wizard.priceIsAskingHint', { locale: 'cs' }),
} as const;

/** Stav behu auditu (BLOK 9) - popisky pro zivy stav v UI. */
export const AUDIT_RUN_STATUS_CAPTIONS: Readonly<Record<string, string>> = {
  QUEUED: t('webAdmin.auditRunStatusCaptions.QUEUED', { locale: 'cs' }),
  RUNNING: t('webAdmin.auditRunStatusCaptions.RUNNING', { locale: 'cs' }),
  NEEDS_REVIEW: t('webAdmin.auditRunStatusCaptions.NEEDS_REVIEW', { locale: 'cs' }),
  DONE: t('webAdmin.auditRunStatusCaptions.DONE', { locale: 'cs' }),
  FAILED: t('webAdmin.auditRunStatusCaptions.FAILED', { locale: 'cs' }),
  CANCELLED: t('webAdmin.auditRunStatusCaptions.CANCELLED', { locale: 'cs' }),
};


/**
 * Panel „Doplnit odpoved" (BLOK 10, Ukol 1): popisky ctyr zdroju odpovedi.
 * `AnswerSourceKind` je sdileny strojovy kod z @reos/duediligence (jedna
 * klasifikace pro web-admin i budouci MCP zapis), popisky zustavaji tady
 * (UI vrstva, vzor OWNER_CAPTIONS/REVIEW_ROLE_CAPTIONS v case-captions.ts).
 */
export const ANSWER_SOURCE_CAPTIONS: Readonly<Record<'CLIENT_STATEMENT' | 'AUTHORITY_STATEMENT' | 'DOCUMENT' | 'BROKER_FINDING', string>> = {
  CLIENT_STATEMENT: t('webAdmin.answerSourceCaptions.CLIENT_STATEMENT', { locale: 'cs' }),
  AUTHORITY_STATEMENT: t('webAdmin.answerSourceCaptions.AUTHORITY_STATEMENT', { locale: 'cs' }),
  DOCUMENT: t('webAdmin.answerSourceCaptions.DOCUMENT', { locale: 'cs' }),
  BROKER_FINDING: t('webAdmin.answerSourceCaptions.BROKER_FINDING', { locale: 'cs' }),
};

export const ANSWER_QUESTION_TEXTS = {
  buttonLabel: t('webAdmin.answerQuestion.buttonLabel', { locale: 'cs' }),
  panelTitle: t('webAdmin.answerQuestion.panelTitle', { locale: 'cs' }),
  textLabel: t('webAdmin.answerQuestion.textLabel', { locale: 'cs' }),
  textPlaceholder: t('webAdmin.answerQuestion.textPlaceholder', { locale: 'cs' }),
  sourceLabel: t('webAdmin.answerQuestion.sourceLabel', { locale: 'cs' }),
  dateLabel: t('webAdmin.answerQuestion.dateLabel', { locale: 'cs' }),
  saveButton: t('webAdmin.answerQuestion.saveButton', { locale: 'cs' }),
  cancelButton: t('webAdmin.answerQuestion.cancelButton', { locale: 'cs' }),
  closeButton: t('webAdmin.answerQuestion.closeButton', { locale: 'cs' }),
  declarativeHint: t('webAdmin.answerQuestion.declarativeHint', { locale: 'cs' }),
  closesGateHint: t('webAdmin.answerQuestion.closesGateHint', { locale: 'cs' }),
  apiUnavailableHint: t('webAdmin.answerQuestion.apiUnavailableHint', { locale: 'cs' }),
  gateUnsupportedHint: t('webAdmin.answerQuestion.gateUnsupportedHint', { locale: 'cs' }),
  roofLabel: t('webAdmin.answerQuestion.roofLabel', { locale: 'cs' }),
  roofOptionTiles: t('webAdmin.answerQuestion.roofOptionTiles', { locale: 'cs' }),
  roofOptionAsbestos: t('webAdmin.answerQuestion.roofOptionAsbestos', { locale: 'cs' }),
  roofOptionMetal: t('webAdmin.answerQuestion.roofOptionMetal', { locale: 'cs' }),
  roofOptionUnknown: t('webAdmin.answerQuestion.roofOptionUnknown', { locale: 'cs' }),
  eaveHeightLabel: t('webAdmin.answerQuestion.eaveHeightLabel', { locale: 'cs' }),
  ridgeHeightLabel: t('webAdmin.answerQuestion.ridgeHeightLabel', { locale: 'cs' }),
  passageKindLabel: t('webAdmin.answerQuestion.passageKindLabel', { locale: 'cs' }),
  passageKindGate: t('webAdmin.answerQuestion.passageKindGate', { locale: 'cs' }),
  passageKindWall: t('webAdmin.answerQuestion.passageKindWall', { locale: 'cs' }),
  passageKindOpen: t('webAdmin.answerQuestion.passageKindOpen', { locale: 'cs' }),
  saving: t('webAdmin.answerQuestion.saving', { locale: 'cs' }),
  savedHint: t('webAdmin.answerQuestion.savedHint', { locale: 'cs' }),
  savedPartialHint: t('webAdmin.answerQuestion.savedPartialHint', { locale: 'cs' }),
  errorPrefix: t('webAdmin.answerQuestion.errorPrefix', { locale: 'cs' }),
  savedNote: (byWhom: string, date: string, source: string): string =>
    t('webAdmin.answerQuestion.savedNote', { locale: 'cs', values: { byWhom, date, source } }),
} as const;

/** Tvrzeni maklere u situacniho planu (krok G4) - budovy a strana koridoru. */
export const BROKER_CLAIM_TEXTS = {
  heading: t('webAdmin.brokerClaim.heading', { locale: 'cs' }),
  hint: t('webAdmin.brokerClaim.hint', { locale: 'cs' }),
  buildingDemolishButton: t('webAdmin.brokerClaim.buildingDemolishButton', { locale: 'cs' }),
  buildingKeepButton: t('webAdmin.brokerClaim.buildingKeepButton', { locale: 'cs' }),
  buildingIntentDemolished: t('webAdmin.brokerClaim.buildingIntentDemolished', { locale: 'cs' }),
  buildingIntentKept: t('webAdmin.brokerClaim.buildingIntentKept', { locale: 'cs' }),
  corridorSideLabel: t('webAdmin.brokerClaim.corridorSideLabel', { locale: 'cs' }),
  corridorSideWest: t('webAdmin.brokerClaim.corridorSideWest', { locale: 'cs' }),
  corridorSideEast: t('webAdmin.brokerClaim.corridorSideEast', { locale: 'cs' }),
  saving: t('webAdmin.brokerClaim.saving', { locale: 'cs' }),
  savedNote: (questionCount: number): string =>
    questionCount > 0
      ? t('webAdmin.brokerClaim.savedNoteSome', { locale: 'cs', values: { count: String(questionCount) } })
      : t('webAdmin.brokerClaim.savedNoteNone', { locale: 'cs' }),
  unavailable: t('webAdmin.brokerClaim.unavailable', { locale: 'cs' }),
  errorPrefix: t('webAdmin.brokerClaim.errorPrefix', { locale: 'cs' }),
} as const;

/** Zalozky faze audit u konceptu (DOPLNEK R.B.): audit jeste nebehl. */
export const AUDIT_NOT_STARTED: PreparingSectionCopy = {
  title: t('webAdmin.auditNotStarted.title', { locale: 'cs' }),
  perex: t('webAdmin.auditNotStarted.perex', { locale: 'cs' }),
  bullets: [
    t('webAdmin.auditNotStarted.bullets.0', { locale: 'cs' }),
    t('webAdmin.auditNotStarted.bullets.1', { locale: 'cs' }),
  ],
};

/** Situacni plan bez komunikace (degraded.missing obsahuje ROAD) — ulice se nedomysli. */
export const SITEPLAN_NO_ROAD_NOTE = t('webAdmin.sitePlan.noRoadNote', { locale: 'cs' });

/**
 * Krok O5a: degradovany plan rekne, co chybi a ktera zjisteni by to doplnila.
 * Klice jsou strojove (`R3SitePlanMissing`), lidsky text patri sem — stejny
 * vzor jako ostatni popisky detailu.
 */
export const SITEPLAN_DEGRADED_TEXTS = {
  heading: t('webAdmin.sitePlan.degradedHeading', { locale: 'cs' }),
  missingFactsLabel: t('webAdmin.sitePlan.missingFactsLabel', { locale: 'cs' }),
} as const;

export const SITEPLAN_MISSING_CAPTIONS: Readonly<Record<string, string>> = {
  ROAD: t('webAdmin.sitePlan.missing.ROAD', { locale: 'cs' }),
  BUILDING: t('webAdmin.sitePlan.missing.BUILDING', { locale: 'cs' }),
  CORRIDOR: t('webAdmin.sitePlan.missing.CORRIDOR', { locale: 'cs' }),
};

/** Krok G3: prepinac scenaru koridoru + radek verze planu pod situacnim planem. */
export const SITEPLAN_SCENARIO_TEXTS = {
  switcherLabel: t('webAdmin.sitePlan.scenario.switcherLabel', { locale: 'cs' }),
  passable: t('webAdmin.sitePlan.scenario.passable', { locale: 'cs' }),
  notPassable: t('webAdmin.sitePlan.scenario.notPassable', { locale: 'cs' }),
} as const;

/** Popisek scenare koridoru — AS_IS vs AFTER_REMOVAL:<ref> (strojovy klic, lidsky text tady). */
export function corridorScenarioCaption(scenario: string): string {
  return scenario === 'AS_IS'
    ? t('webAdmin.sitePlan.corridorScenarioCaption.asIs', { locale: 'cs' })
    : t('webAdmin.sitePlan.corridorScenarioCaption.afterRemoval', { locale: 'cs' });
}

export function sitePlanVersionLine(version: {
  readonly geometryVersion: number;
  readonly generatedAt: string;
}, sources: readonly string[]): string {
  const planPrefix = t('webAdmin.sitePlan.versionLine.planPrefix', {
    locale: 'cs',
    values: { version: String(version.geometryVersion) },
  });
  const sourcesText = sources.length > 0
    ? t('webAdmin.sitePlan.versionLine.sourcesPrefix', { locale: 'cs', values: { sources: sources.join(', ') } })
    : '';
  const asOf = t('webAdmin.sitePlan.versionLine.asOf', {
    locale: 'cs',
    values: { date: new Date(version.generatedAt).toLocaleDateString('cs-CZ') },
  });
  return `${planPrefix}${sourcesText}${asOf}`;
}

/**
 * Nahledy podkladu JSOU zapnute (rozhodnuti R.B. 2. 9. 2026 pro prezentaci
 * makleřce: jsou to jeji vlastni podklady, odkaz je neverejny). Podklady ale
 * obsahuji osobni udaje (jmeno vlastnice na LV), takze:
 *  - stranka je noindex/nofollow a robots.txt zakazuje cely web,
 *  - soubory NEJSOU v gitu, lezi jen na preview serveru,
 *  - prihlaseni je dalsi krok a pak se tahle poznamka zmeni na kontrolu opravneni.
 */
export const INPUTS_PRIVACY_NOTE = t('webAdmin.inputsPrivacyNote', { locale: 'cs' });

/**
 * Ctyri ruzne stavy, ktere se driv michaly do jednoho „audit dokoncen"
 * (Milnik 1, bod 1). Kazdy odpovida na jinou otazku.
 */
export const CASE_STATUS_LABELS = {
  researchRun: t('webAdmin.caseStatusLabels.researchRun', { locale: 'cs' }),
  caseState: t('webAdmin.caseStatusLabels.caseState', { locale: 'cs' }),
  readiness: t('webAdmin.caseStatusLabels.readiness', { locale: 'cs' }),
  publishedRevision: t('webAdmin.caseStatusLabels.publishedRevision', { locale: 'cs' }),
} as const;

export const CASE_STATE_WAITING = t('webAdmin.caseStateWaiting', { locale: 'cs' });

/**
 * `dd_trust_assessments.revision` je dnes jen `f<pocet faktu>`, ne immutable
 * revize pripadu — nesmi se prezentovat jako publikovana revize auditu
 * (Milnik 1, bod 4). Skutecne revize prijdou s modelem immutable revisions.
 */
export const CASE_REVISION_NOT_AVAILABLE = t('webAdmin.caseRevision.notAvailable', { locale: 'cs' });
export const CASE_REVISION_SNAPSHOT_HINT = t('webAdmin.caseRevision.snapshotHint', { locale: 'cs' });

/** Rozporny vstup bez evidence — nesmi se zobrazit jako potvrzeny fakt. */
export const UNVERIFIED_CONTRADICTORY_INPUT_LABEL = t('webAdmin.unverifiedContradictoryInputLabel', { locale: 'cs' });

export const CLAIM_CONFLICT_CAPTIONS = {
  // Prazdny stav neni text k prekladu — zadny konflikt se nezobrazuje vubec.
  NONE: '',
  CONFLICT: t('webAdmin.claimConflictCaptions.CONFLICT', { locale: 'cs' }),
  UNVERIFIED_CONTRADICTORY_INPUT: UNVERIFIED_CONTRADICTORY_INPUT_LABEL,
  NEEDS_REVALIDATION: t('webAdmin.claimConflictCaptions.NEEDS_REVALIDATION', { locale: 'cs' }),
} as const;

/** Technicke klice a identifikatory patri pod rozbaleny detail, ne do hlavniho textu. */
export const TECHNICAL_DETAIL_SUMMARY = t('webAdmin.technicalDetailSummary', { locale: 'cs' });

export const AREA_LABELS = {
  knCandidate: t('webAdmin.areaLabels.knCandidate', { locale: 'cs' }),
  manualInput: t('webAdmin.areaLabels.manualInput', { locale: 'cs' }),
  confirmed: t('webAdmin.areaLabels.confirmed', { locale: 'cs' }),
  difference: t('webAdmin.areaLabels.difference', { locale: 'cs' }),
  notConfirmed: t('webAdmin.areaLabels.notConfirmed', { locale: 'cs' }),
} as const;

/**
 * Nahlásit problém (widget v adminu). Tiket jde rovnou na RedHand podporu;
 * text nikde neslibuje odpoved v case, kterou nemame jak dodrzet.
 */
export const SUPPORT_TEXTS = {
  openButton: t('webAdmin.support.openButton', { locale: 'cs' }),
  title: t('webAdmin.support.title', { locale: 'cs' }),
  hint: t('webAdmin.support.hint', { locale: 'cs' }),
  severityLabel: t('webAdmin.support.severityLabel', { locale: 'cs' }),
  subjectLabel: t('webAdmin.support.subjectLabel', { locale: 'cs' }),
  bodyLabel: t('webAdmin.support.bodyLabel', { locale: 'cs' }),
  contextNote: t('webAdmin.support.contextNote', { locale: 'cs' }),
  send: t('webAdmin.support.send', { locale: 'cs' }),
  sending: t('webAdmin.support.sending', { locale: 'cs' }),
  close: t('webAdmin.support.close', { locale: 'cs' }),
  sent: t('webAdmin.support.sent', { locale: 'cs' }),
  sentDuplicate: t('webAdmin.support.sentDuplicate', { locale: 'cs' }),
  failed: t('webAdmin.support.failed', { locale: 'cs' }),
  offline: t('webAdmin.support.offline', { locale: 'cs' }),
  validation: t('webAdmin.support.validation', { locale: 'cs' }),
  /** Krok MOB1 bod 3 — snimky obrazovky u hlaseni. */
  screenshotsLabel: t('webAdmin.support.screenshotsLabel', { locale: 'cs' }),
  addScreenshot: t('webAdmin.support.addScreenshot', { locale: 'cs' }),
  removeScreenshot: t('webAdmin.support.removeScreenshot', { locale: 'cs' }),
  screenshotAlt: t('webAdmin.support.screenshotAlt', { locale: 'cs' }),
  screenshotFailed: t('webAdmin.support.screenshotFailed', { locale: 'cs' }),
} as const;

/**
 * Krok MOB1 bod 2 — pruh „Je k dispozici nová verze". Stoji NAD routerem
 * (`main.tsx`), tedy mimo `LocaleProvider`, proto cte cestinu stejne jako
 * `SUPPORT_TEXTS`.
 */
export const UPDATE_TEXTS = {
  banner: t('webAdmin.update.banner', { locale: 'cs' }),
  action: t('webAdmin.update.action', { locale: 'cs' }),
  dismiss: t('webAdmin.update.dismiss', { locale: 'cs' }),
} as const;

export const SUPPORT_SEVERITIES = [
  { id: 'P1', label: t('webAdmin.supportSeverities.P1.label', { locale: 'cs' }) },
  { id: 'P2', label: t('webAdmin.supportSeverities.P2.label', { locale: 'cs' }) },
  { id: 'P3', label: t('webAdmin.supportSeverities.P3.label', { locale: 'cs' }) },
  { id: 'P4', label: t('webAdmin.supportSeverities.P4.label', { locale: 'cs' }) },
] as const;

export type SupportSeverity = (typeof SUPPORT_SEVERITIES)[number]['id'];

/**
 * Schvalovani nastroju MCP (krok M1). Makler pozada Claude o zapis, tady ho
 * clovek potvrdi nebo zamitne. Zadny text nesmi tvrdit, ze se uz neco stalo -
 * dokud neni schvaleno, nic se nezapsalo.
 */
export const APPROVALS_TEXTS = {
  title: t('webAdmin.approvals.title', { locale: 'cs' }),
  perex: t('webAdmin.approvals.perex', { locale: 'cs' }),
  filterLabel: t('webAdmin.approvals.filterLabel', { locale: 'cs' }),
  filterAll: t('webAdmin.approvals.filterAll', { locale: 'cs' }),
  requestedAtLabel: t('webAdmin.approvals.requestedAtLabel', { locale: 'cs' }),
  reload: t('webAdmin.approvals.reload', { locale: 'cs' }),
  loading: t('webAdmin.approvals.loading', { locale: 'cs' }),
  empty: t('webAdmin.approvals.empty', { locale: 'cs' }),
  approve: t('webAdmin.approvals.approve', { locale: 'cs' }),
  reject: t('webAdmin.approvals.reject', { locale: 'cs' }),
  stepUpPrompt: t('webAdmin.approvals.stepUpPrompt', { locale: 'cs' }),
  decisionReason: t('webAdmin.approvals.decisionReason', { locale: 'cs' }),
  columns: {
    id: t('webAdmin.approvals.columns.id', { locale: 'cs' }),
    tool: t('webAdmin.approvals.columns.tool', { locale: 'cs' }),
    status: t('webAdmin.approvals.columns.status', { locale: 'cs' }),
    requestedBy: t('webAdmin.approvals.columns.requestedBy', { locale: 'cs' }),
    requestedAt: t('webAdmin.approvals.columns.requestedAt', { locale: 'cs' }),
    actions: t('webAdmin.approvals.columns.actions', { locale: 'cs' }),
  },
  statuses: {
    pending: t('webAdmin.approvals.statuses.pending', { locale: 'cs' }),
    approved: t('webAdmin.approvals.statuses.approved', { locale: 'cs' }),
    rejected: t('webAdmin.approvals.statuses.rejected', { locale: 'cs' }),
    executed: t('webAdmin.approvals.statuses.executed', { locale: 'cs' }),
  },
  errorPrefix: t('webAdmin.approvals.errorPrefix', { locale: 'cs' }),
} as const;

export type ApprovalStatus = keyof typeof APPROVALS_TEXTS.statuses;

/**
 * `CASE_KIND_ILLUSTRATION` jsou cesty k souborum (ne text) — zustavaji jako
 * plain konstanta, i18n katalog se na cesty pod /public nevztahuje.
 */
/** Typ predmetu zakazky — popisek k `CASE_KIND_ILLUSTRATION` (drive lib/case-list.ts). */
export const CASE_KIND_CAPTIONS: Readonly<Record<'LAND' | 'HOUSE' | 'APARTMENT' | 'OTHER', string>> = {
  LAND: t('webAdmin.caseKind.LAND', { locale: 'cs' }),
  HOUSE: t('webAdmin.caseKind.HOUSE', { locale: 'cs' }),
  APARTMENT: t('webAdmin.caseKind.APARTMENT', { locale: 'cs' }),
  OTHER: t('webAdmin.caseKind.OTHER', { locale: 'cs' }),
};

export const CASE_KIND_ILLUSTRATION: Readonly<Record<'LAND' | 'HOUSE' | 'APARTMENT' | 'OTHER', string>> = {
  LAND: '/ilustrace/pozemek.png',
  HOUSE: '/ilustrace/dum.png',
  APARTMENT: '/ilustrace/byt.png',
  OTHER: '/ilustrace/jine.png',
};
